// frontend/src/utils/forecastRows.js
//
// `/api/forecasts/` rows → the rows a SaaS forecast grid renders: the whole
// pivot (`buildForecastRows`) and the per-month keys one row carries
// (`applyForecastMonthCell`).
//
// This is where the consensus/promotion/final split lands on the client, and
// every renderer and cellClassRule downstream reads the keys it produces, so it
// is pure and unit-tested apart from the SSRM grid.

import { itemLocationRowId } from './itemLocationRowId';
//
// ── WHAT EACH KEY IS FOR ────────────────────────────────────────────────────
//   <month>              THE CELL. The final — consensus plus the promotion's
//                        pinned contribution. Computed by the server
//                        (`FINAL_FORECAST_UNITS`) and never re-derived here, so
//                        the cell, the pinned totals row, the month-column sort
//                        and every report read one number.
//   <month>_consensus    the merchant's own figure, promotion excluded. NOT the
//                        override test — it holds the ENGINE's number whenever
//                        nobody has overridden the month (see below).
//   <month>_user_modified  whether a PERSON put the consensus there. Drives the
//                        user ink, which asks exactly that question — and
//                        which a promotion the merchant scheduled does not
//                        answer yes to.
//                        Server-owned (`ConsensusUserModified`, stamped by
//                        `write_final_units`), never inferred as
//                        `consensus !== statistical`: a merchant can pin a
//                        month to the engine's own figure, and that is still
//                        an override.
//   <month>_system       what the cell shows once the override is cleared —
//                        the engine's number plus the promotion. Computed by
//                        the server (`SYSTEM_FORECAST_UNITS`) and never
//                        re-derived here, so this grid and the Reorder levels
//                        popup read one number. Null where Manexys has no
//                        forecast for the month.
//   <month>_statistical  the engine's number, the tooltip's first line. Null
//                        where Manexys has no forecast — the tooltip reads
//                        null as "nothing to explain".
//   <month>_promotion    the promotion's contribution, or null when none covers
//                        the month. Drives `ag-cell-note-mark` and the
//                        tooltip's promotion line. NULL and 0 are different
//                        answers, so every consumer must test `!= null` rather
//                        than truthiness.
//   <month>_forecast     the raw row. `forecastBatches` reads its `ForecastID`
//                        to decide update-vs-create.

/**
 * @param {object|undefined} match  the `/api/forecasts/` row for this month,
 *   or undefined when the engine has not written it.
 * @returns {{value: number|null, system: number|null, consensus: number|null,
 *   statistical: number|null, promotion: number|null, userModified: boolean,
 *   forecast: object|null}}
 */
export function forecastMonthCell(match) {
  if (!match) {
    // Absent is not zero. The cell renders "—" and the save turns an edit here
    // into a create; a 0 would read as a real forecast of no demand. Nobody has
    // overridden a month that has no row.
    return {
      value: null,
      system: null,
      consensus: null,
      statistical: null,
      promotion: null,
      userModified: false,
      forecast: null,
    };
  }
  return {
    value: match.FinalForecastUnits,
    system: match.SystemForecastUnits,
    consensus: match.ConsensusForecastUnits,
    statistical: match.StatisticalForecastUnits,
    promotion: match.PromotionForecastUnits,
    userModified: match.ConsensusUserModified,
    forecast: match,
  };
}

/**
 * Write one month's keys onto a grid row, in place.
 *
 * Kept beside the mapper so the key NAMES have one home too — the renderers,
 * cellClassRules and tooltip all read these suffixes, and a rename that missed
 * one would fail silently as an undefined cell rather than as an error.
 */
export function applyForecastMonthCell(row, monthKey, match) {
  const cell = forecastMonthCell(match);
  row[monthKey] = cell.value;
  row[`${monthKey}_system`] = cell.system;
  row[`${monthKey}_consensus`] = cell.consensus;
  row[`${monthKey}_statistical`] = cell.statistical;
  row[`${monthKey}_promotion`] = cell.promotion;
  row[`${monthKey}_user_modified`] = cell.userModified;
  row[`${monthKey}_forecast`] = cell.forecast;
  return row;
}

/**
 * The whole grid body: many `/api/forecasts/` rows (one per item-location-month)
 * → one row per item-location, months pivoted into columns.
 *
 * Identity fields are carried through exactly as the wire sent them. Item and
 * Location codes and names are NOT NULL columns; a SKU is legitimately absent
 * (the server sends the item's article code, '' when it has none) and how that
 * DISPLAYS is settled once, in the column (`optionalTextColumn`) and in
 * ProductIdentity's "No SKU" line.
 *
 * @param {Array}  forecasts               serialized forecast rows.
 * @param {object} options
 * @param {string[]} options.monthKeys     'YYYY-MM' columns to pivot into.
 * @param {object|null} options.salesHistory   {`${itemId}_${locationId}`: {'YYYY-MM': units}}.
 * @param {string[]|null} options.historyMonths  history columns to fill.
 * @returns {Array} grid rows.
 */
export function buildForecastRows(forecasts, { monthKeys, salesHistory, historyMonths }) {
  const grouped = new Map();

  forecasts.forEach((f) => {
    const key = itemLocationRowId(f.item_code, f.location_code);

    if (!grouped.has(key)) {
      grouped.set(key, {
        rowId: key,
        ItemCode: f.item_code,
        ItemName: f.item_name,
        ItemSKU: f.item_sku,
        ImageUrl: f.image_url,
        LocationCode: f.location_code,
        LocationName: f.location_name,
        ItemId: f.item_id,
        LocationId: f.location_id,
        shopify_product_gid: f.shopify_product_gid,
        shopify_product_title: f.shopify_product_title,
        shopify_variant_title: f.shopify_variant_title,
        forecasts: [],
      });
    }
    grouped.get(key).forecasts.push(f);
  });

  return Array.from(grouped.values(), ({ forecasts: months, ...row }) => {
    monthKeys.forEach((monthKey) => {
      applyForecastMonthCell(
        row,
        monthKey,
        months.find((f) => f.ForecastDate.substring(0, 7) === monthKey),
      );
    });

    if (salesHistory && historyMonths) {
      const itemHistory = salesHistory[`${row.ItemId}_${row.LocationId}`];
      historyMonths.forEach((monthKey) => {
        // An item with no sales in a month has no entry; the column reads
        // null as "nothing sold here", the same answer either way.
        const units = itemHistory?.[monthKey];
        row[`hist_${monthKey}`] = units !== undefined ? units : null;
      });
    }

    return row;
  });
}
